/** Local arm cross-sections for the elbow station tool. Triangle intersections
 * give an area centroid independent of tessellation density. The UI chooses the
 * station; this module measures the interior and refuses open/ambiguous sections.
 */
import { Quaternion, Vector3, type Object3D } from 'three';
import { skinnedMeshesOfSelectedSkeleton } from '@harryhausen/adapter-three';
import type { RigDescriptor } from '@harryhausen/kernel';

export function elbowTube(
  scene: Object3D,
  rig: RigDescriptor,
  side: 'Left' | 'Right',
  selectSkeleton?: string,
) {
  const mapped = (id: string) => rig.mapping.find((m) => m.ontologyId === `${side}${id}`)?.rigBone;
  const driver = mapped('ForeArm'),
    parent = mapped('Arm'),
    end = mapped('Hand');
  const bone = rig.bones.find((b) => b.name === driver);
  const shoulder = rig.bones.find((b) => b.name === parent),
    wrist = rig.bones.find((b) => b.name === end);
  if (!bone || !shoulder || !wrist || !driver || !parent)
    throw new Error(`${side} arm is not mapped`);
  const joint = new Vector3().copy(bone.worldBind.position);
  const axis = new Vector3()
    .copy(wrist.worldBind.position)
    .sub(shoulder.worldBind.position)
    .normalize();
  const length = joint.distanceTo(wrist.worldBind.position);
  const u = new Vector3()
    .crossVectors(axis, Math.abs(axis.y) < 0.9 ? new Vector3(0, 1, 0) : new Vector3(1, 0, 0))
    .normalize();
  const v = new Vector3().crossVectors(axis, u).normalize();
  const triangles: Vector3[][] = [];
  for (const mesh of skinnedMeshesOfSelectedSkeleton(scene, {
    label: rig.label,
    ...(selectSkeleton === undefined ? {} : { selectSkeleton }),
  })) {
    const p = mesh.geometry.getAttribute('position'),
      si = mesh.geometry.getAttribute('skinIndex'),
      sw = mesh.geometry.getAttribute('skinWeight');
    const isArm = (name: string) =>
      name === driver ||
      name === parent ||
      name.startsWith(`${driver}Twist`) ||
      name.startsWith(`${parent}Twist`) ||
      (name.endsWith('_corr') && rig.bones.find((b) => b.name === name)?.parent === parent) ||
      (name.endsWith('_corr') && rig.bones.find((b) => b.name === name)?.parent === driver);
    const slots = new Set(mesh.skeleton.bones.flatMap((b, i) => (isArm(b.name) ? [i] : [])));
    const points = Array.from({ length: p.count }, (_, i) =>
      new Vector3().fromBufferAttribute(p, i).applyMatrix4(mesh.bindMatrix),
    );
    const member = points.map((_, i) => {
      let weight = 0;
      for (let k = 0; k < sw.itemSize; k++)
        if (slots.has(si.getComponent(i, k))) weight += sw.getComponent(i, k);
      return weight >= 0.25;
    });
    const index = mesh.geometry.index;
    for (let i = 0; i < (index?.count ?? p.count); i += 3) {
      const ids = [0, 1, 2].map((k) => index?.getX(i + k) ?? i + k);
      if (ids.every((n) => member[n])) triangles.push(ids.map((n) => points[n]!));
    }
  }
  return {
    driver,
    joint,
    axis,
    u,
    v,
    length,
    triangles,
    rotation: new Quaternion().copy(bone.worldBind.rotation),
  };
}
export type ElbowTube = ReturnType<typeof elbowTube>;

/** Keep manual cross-section offsets inside the actual polygon, including thin sections. */
export function insideElbowSection(
  tube: ElbowTube,
  contour: readonly Vector3[],
  point: Vector3,
): boolean {
  const x = point.dot(tube.u),
    y = point.dot(tube.v);
  let inside = false;
  for (let i = 0, j = contour.length - 1; i < contour.length; j = i++) {
    const a = contour[i]!,
      b = contour[j]!;
    const ax = a.dot(tube.u),
      ay = a.dot(tube.v),
      bx = b.dot(tube.u),
      by = b.dot(tube.v);
    if (ay > y !== by > y && x < ((bx - ax) * (y - ay)) / (by - ay) + ax) inside = !inside;
  }
  return inside;
}

export function elbowSection(tube: ElbowTube, fraction: number) {
  if (!Number.isFinite(fraction) || Math.abs(fraction) > 0.3)
    throw new Error('Keep the marker near the existing elbow');
  const station = tube.joint.clone().addScaledVector(tube.axis, fraction * tube.length);
  const tolerance = tube.length * 1e-5;
  const nodes: Vector3[] = [],
    neighbors: Set<number>[] = [];
  const node = (point: Vector3) => {
    const found = nodes.findIndex((p) => p.distanceToSquared(point) < tolerance * tolerance);
    if (found >= 0) return found;
    nodes.push(point);
    neighbors.push(new Set());
    return nodes.length - 1;
  };
  for (const triangle of tube.triangles) {
    const hits: Vector3[] = [];
    for (let e = 0; e < 3; e++) {
      const a = triangle[e]!,
        b = triangle[(e + 1) % 3]!;
      const da = a.clone().sub(station).dot(tube.axis),
        db = b.clone().sub(station).dot(tube.axis);
      if ((da <= 0 && db > 0) || (db <= 0 && da > 0)) hits.push(a.clone().lerp(b, da / (da - db)));
    }
    if (hits.length !== 2 || hits[0]!.distanceTo(hits[1]!) < tolerance) continue;
    const a = node(hits[0]!),
      b = node(hits[1]!);
    if (a !== b) {
      neighbors[a]!.add(b);
      neighbors[b]!.add(a);
    }
  }
  const visited = new Set<number>();
  const loops: Vector3[][] = [];
  for (let seed = 0; seed < nodes.length; seed++) {
    if (visited.has(seed)) continue;
    const ids: number[] = [];
    let current = seed,
      previous = -1;
    do {
      if (neighbors[current]!.size !== 2)
        throw new Error('Arm cross-section is open or branches here; move the marker slightly');
      visited.add(current);
      ids.push(current);
      const next = [...neighbors[current]!].find((n) => n !== previous)!;
      previous = current;
      current = next;
    } while (current !== seed && !visited.has(current));
    if (current === seed && ids.length >= 3) loops.push(ids.map((i) => nodes[i]!));
  }
  const sections = loops
    .map((contour) => {
      let area2 = 0,
        cx = 0,
        cy = 0;
      for (let i = 0; i < contour.length; i++) {
        const a = contour[i]!.clone().sub(station),
          b = contour[(i + 1) % contour.length]!.clone().sub(station);
        const ax = a.dot(tube.u),
          ay = a.dot(tube.v),
          bx = b.dot(tube.u),
          by = b.dot(tube.v);
        const cross = ax * by - bx * ay;
        area2 += cross;
        cx += (ax + bx) * cross;
        cy += (ay + by) * cross;
      }
      if (Math.abs(area2) < tolerance * tolerance)
        throw new Error('Arm cross-section has no measurable area');
      const center = station
        .clone()
        .addScaledVector(tube.u, cx / (3 * area2))
        .addScaledVector(tube.v, cy / (3 * area2));
      return { center, contour, radius: Math.sqrt(Math.abs(area2) / (2 * Math.PI)) };
    })
    .sort((a, b) => a.center.distanceToSquared(station) - b.center.distanceToSquared(station));
  if (sections.length !== 1)
    throw new Error(`Expected one arm tube at the marker; found ${sections.length}`);
  return sections[0]!;
}
